import { ClerkProvider } from "@clerk/tanstack-react-start";
import { shadcn } from "@clerk/ui/themes";
import { PolicyStack } from "@policystack/react/provider";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import { HeadContent, Scripts, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { createMiddleware } from "@tanstack/react-start";
import { evlogErrorHandler } from "evlog/nitro/v3";

import { AnonymousPlayerIdSync } from "#/components/AnonymousPlayerIdSync";
import { CookieConsent } from "#/components/CookieConsent";
import { PageBackgroundGate } from "#/components/PageBackgroundGate";
import { PostHogAnalytics } from "#/components/PostHogAnalytics";
import { DefaultRouteNotFoundComponent } from "#/components/RouteFallback";
import { Toaster } from "#/components/ui/sonner";
import {
  persistAnonymousPlayerIdFromSession,
  readAnonymousPlayerId,
} from "#/lib/anonymousPlayerId";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import { GlobalStatsIntroProvider } from "#/lib/globalStatsIntro";
import { myEntitlementsQueryOptions } from "#/lib/queries";
import { loadRootSession } from "#/server/loadRootSession";

import Footer from "../components/Footer";
import Header from "../components/Header";
import config from "../policystack";

import appCss from "../styles.css?url";

export interface AppRouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<AppRouterContext>()({
  server: {
    middleware: [createMiddleware().server(evlogErrorHandler)],
  },
  loader: async ({ context }) => {
    const clientPlayerId = readAnonymousPlayerId();
    const session = await loadRootSession({
      data: clientPlayerId ? { playerId: clientPlayerId } : {},
    });
    persistAnonymousPlayerIdFromSession(session);
    if (!session.isAnonymous) {
      context.queryClient.setQueryData(
        myEntitlementsQueryOptions(session.playerId).queryKey,
        session.entitlements,
      );
    }
    return { session };
  },
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        name: "theme-color",
        content: "#2563eb",
      },
      ...documentHead(DOCUMENT_TITLES.home).meta,
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/png",
        href: "/favicon-96x96.png",
        sizes: "96x96",
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon.svg",
      },
      {
        rel: "shortcut icon",
        href: "/favicon.ico",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "manifest",
        href: "/site.webmanifest",
      },
    ],
  }),
  notFoundComponent: DefaultRouteNotFoundComponent,
  shellComponent: RootDocument,
});

function RootAppChrome({ children }: { children: React.ReactNode }) {
  return (
    <>
      <PageBackgroundGate />
      <div className="relative flex min-h-dvh flex-col">
        <Header />
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        <Footer />
      </div>
    </>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased selection:bg-primary/25">
        <PolicyStack config={config}>
          <ClerkProvider
            appearance={{
              theme: shadcn,
            }}
          >
            <GlobalStatsIntroProvider>
              <PostHogAnalytics>
                <AnonymousPlayerIdSync />
                <RootAppChrome>{children}</RootAppChrome>
              </PostHogAnalytics>
              <CookieConsent />
              <Toaster />
            </GlobalStatsIntroProvider>
          </ClerkProvider>
        </PolicyStack>
        <TanStackDevtools
          config={{
            position: "bottom-right",
          }}
          plugins={[
            {
              name: "Tanstack Router",
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
        <Scripts />
      </body>
    </html>
  );
}
